import { z } from "zod";
import { ESIGN_AUTOMATION_CODES } from "./BasicData/automationCodes";

const translationPath = "4uESign.page.automations.automationRules.errors.";
const genericSchema = (errorPath: string) =>
  z.object(
    {
      id: z.string(),
      name: z.string(),
      code: z.string().nullish(),
      description: z.string().nullish(),
    },
    { invalid_type_error: translationPath + errorPath },
  );

export const esignRuleSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, translationPath + "name"),
    initialDate: z.date({
      invalid_type_error: translationPath + "initial_date",
    }),
    endDate: z.date({ invalid_type_error: translationPath + "end_date" }),
    enable: z.boolean(),
    priority: z.coerce.number().min(1, translationPath + "priority"),
    signOnlyPeriod: z.coerce.number().nullish(),
    observation: z.string().optional().nullish(),
    esignAutomation: genericSchema("automation"),
    physicians: z.array(genericSchema("physicians")).nullish(),
    insuranceTypes: z.array(genericSchema("insuranceTypes")).nullish(),
    authorizationTypes: z.array(genericSchema("authorizationTypes")).nullish(),
    guideTypes: z.array(genericSchema("guideTypes")).nullish(),
    authorizationStages: z
      .array(genericSchema("authorizationStages"))
      .nullish(),
    insurances: z.array(genericSchema("insurances")).nullish(),
    esignEstablishments: z.array(genericSchema("establishment")).nullish(),
    esignAttendantTypes: z
      .array(genericSchema("esignAttendantTypes"))
      .nullish(),
    esignLocationSaveFile: genericSchema("esignLocationSaveFile"),
    requiredSignatures: z
      .array(genericSchema("requiredSignatures"))
      .optional()
      .nullish(),
    additionalData: z.string().optional().nullish(),
    fileTypeCodeIntegrationId: z.string().optional().nullish(),
    technicalManagerCode: z.string().max(50).optional().nullish(),
    headerPdf: z.string().optional().nullish(),
    footerPdf: z.string().optional().nullish(),
    signaturePdf: z.string().optional().nullish(),
    order: z.string().optional().nullish(),
    saveQrCode: z.boolean(),
    isAdditionalDataRequired: z.boolean().optional(),
    signOnly: genericSchema("signOnly"),
  })
  .superRefine((val, ctx) => {
    if (val.initialDate && val.endDate && val.initialDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "initial_date_bigger",
        path: ["initialDate"],
      });
    }

    if (!val.requiredSignatures || val.requiredSignatures.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: "array",
        inclusive: true,
        message: translationPath + "requiredSignatures_empty",
        path: ["requiredSignatures"],
      });
    }

    if (
      val.requiredSignatures?.some((signature) => signature.code === "4") &&
      !val.technicalManagerCode?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "technicalManagerCode",
        path: ["technicalManagerCode"],
      });
    }

    if (val.isAdditionalDataRequired) {
      if (
        !val.fileTypeCodeIntegrationId ||
        val.fileTypeCodeIntegrationId.trim() === ""
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "additionalInfo",
          path: ["fileTypeCodeIntegrationId"],
        });
      }
    }

    if (val.signOnly?.code === "2") {
      if (!val.signOnlyPeriod || val.signOnlyPeriod <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "signOnlyPeriod",
          path: ["signOnlyPeriod"],
        });
      }
    }
  });
export type TESignRuleForm = z.infer<typeof esignRuleSchema>;

export const interoperabilitySchema = z.object({
  id: z.string().optional(),
  service: genericSchema("service"),
  direction: genericSchema("direction"),
});

export type TInteroperabilityForm = z.infer<typeof interoperabilitySchema>;

const mediaFileSchema = z.object({
  id: z.string(),
  mediaTypeId: z.string().optional(),
  isPublic: z.boolean().optional(),
  fileName: z.string().optional(),
  cdnDomain: z.string().optional(),
  filePath: z.string().optional(),
  mimeType: z.string().optional(),
  fileSize: z.number().optional(),
});

const documentSchemaBase = z.object({
  id: z.string().optional(),
  name: z
    .string({ required_error: translationPath + "name" })
    .min(1, translationPath + "name"),
  order: z.coerce.number().min(1, translationPath + "order"),
  enable: z.boolean(),
  initialDate: z.date({
    required_error: translationPath + "initial_date",
    invalid_type_error: translationPath + "initial_date",
  }),
  endDate: z.date({
    required_error: translationPath + "end_date",
    invalid_type_error: translationPath + "end_date",
  }),
  headerPdf: z.string().optional().nullish(),
  bodyPdf: z.string().optional().nullish(),
  footerPdf: z.string().optional().nullish(),
  file: mediaFileSchema.optional().nullish(),
  headerFooterType: genericSchema("headerFooterType").nullish(),
  esignRuleDocExtMedCertTypes: z.array(
    genericSchema("esignRuleDocExtMedCertTypes"),
  ),
});

export const getDocumentSchema = (esignAutomationCode?: string) =>
  documentSchemaBase.superRefine((val, ctx) => {
    if (val.initialDate && val.endDate && val.initialDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "initial_date_bigger",
        path: ["initialDate"],
      });
    }

    if (
      esignAutomationCode === ESIGN_AUTOMATION_CODES.receita &&
      !val.headerFooterType
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "headerFooterType",
        path: ["headerFooterType"],
      });
    }

    if (val.headerFooterType?.code === "2" && !val.headerPdf?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "header_pdf",
        path: ["headerPdf"],
      });
    }
  });

export const documentSchema = getDocumentSchema();

export type TDocumentForm = z.infer<typeof documentSchema>;
